import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { BaseModal } from '../../common/UI/BaseModal';
import { type Column, StandardTable } from '../../common/UI/StandardTable';
import type { Invoice } from './useBillingData';
import { formatBillingName, getRowColor } from './utils';

interface InvoiceDetailsDialogProps {
  invoice: Invoice | null;
  onClose: () => void;
  teamsMap: Record<string, string>;
  usersMap: Record<string, string>;
}

export const InvoiceDetailsDialog: React.FC<InvoiceDetailsDialogProps> = ({
  invoice,
  onClose,
  teamsMap,
  usersMap,
}) => {
  const theme = useTheme();

  if (!invoice) {
    return null;
  }

  const funcColumns: Column<{ name: string; cost: number }>[] = [
    {
      id: 'name',
      label: '', // No header text
      render: (row) => (
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
          {row.name}
        </Typography>
      ),
    },
    {
      id: 'cost',
      label: '',
      align: 'right',
      render: (row) => (
        <Typography variant="body1">
          $
          {row.cost.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Typography>
      ),
    },
  ];

  const teamColumns: Column<{ name: string; cost: number }>[] = [
    {
      id: 'name',
      label: '',
      render: (row) => row.name,
    },
    {
      id: 'cost',
      label: '',
      align: 'right',
      render: (row) =>
        `$${row.cost.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const userColumns: Column<{ name: string; cost: number }>[] = [
    {
      id: 'name',
      label: '',
      render: (row) => row.name,
    },
    {
      id: 'cost',
      label: '',
      align: 'right',
      render: (row) =>
        `$${row.cost.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
  ];

  const funcData = invoice.costsByFunction
    ? Object.entries(
        Object.entries(invoice.costsByFunction).reduce(
          (acc, [funcName, cost]) => {
            const formattedName = formatBillingName(funcName);
            acc[formattedName] = (acc[formattedName] || 0) + cost;
            return acc;
          },
          {} as Record<string, number>
        )
      )
        .sort(([, costA], [, costB]) => costB - costA)
        .map(([name, cost]) => ({ name, cost }))
    : [];

  const teamData = invoice.costsByTeam
    ? Object.entries(invoice.costsByTeam)
        .sort(([, costA], [, costB]) => costB - costA)
        .map(([teamId, cost]) => ({ name: teamsMap[teamId] || teamId, cost }))
    : [];

  const userData = invoice.costsByUser
    ? Object.entries(invoice.costsByUser)
        .sort(([, costA], [, costB]) => costB - costA)
        .map(([userId, cost]) => ({ name: usersMap[userId] || userId, cost }))
    : [];

  return (
    <BaseModal
      open={Boolean(invoice)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={`Invoice Details: ${invoice.period_end}`}
      actions={
        <>
          <Box />
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Status
        </Typography>
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            backgroundColor: invoice.status === 'paid' ? 'success.light' : 'warning.light',
            color: invoice.status === 'paid' ? 'success.contrastText' : 'warning.contrastText',
          }}
        >
          {invoice.status}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" gutterBottom>
        Breakdown
      </Typography>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant="body2" color="text.secondary">
            Total Credits Billed
          </Typography>
          <Typography variant="body1">
            {invoice.credits_billed?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || 'N/A'}
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="body2" color="text.secondary">
            Cost per Credit
          </Typography>
          <Typography variant="body1">
            $
            {invoice.cost_per_credit?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || 'N/A'}{' '}
            {invoice.currency}
          </Typography>
        </Grid>
      </Grid>

      {(!invoice.costsByFunction || Object.keys(invoice.costsByFunction).length === 0) && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Detailed breakdown by function, team, and user is not available for older invoices.
          </Typography>
        </Box>
      )}

      {funcData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Cost by Function
          </Typography>
          <StandardTable
            columns={funcColumns}
            data={funcData}
            keyExtractor={(row) => row.name}
            stickyHeader={false}
            rowSx={(row) => ({ backgroundColor: getRowColor(theme, row.name) })}
          />
        </Box>
      )}

      {teamData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Cost by Team
          </Typography>
          <StandardTable
            columns={teamColumns}
            data={teamData}
            keyExtractor={(row) => row.name}
            stickyHeader={false}
          />
        </Box>
      )}

      {userData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Cost by User
          </Typography>
          <StandardTable
            columns={userColumns}
            data={userData}
            keyExtractor={(row) => row.name}
            stickyHeader={false}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Total Amount</Typography>
        <Typography variant="h6" color="primary.main">
          $
          {invoice.total_amount?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          {invoice.currency}
        </Typography>
      </Box>
    </BaseModal>
  );
};
